import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { login, signup, notesFromServer, cacheUserDataToLocalStorage } from '../lib/authSync';
import { setStorageMode, isLocalMode } from '../lib/storageMode';
const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:10000';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next') === '/subscribe' ? '/subscribe' : '/app';
  const [mode, setMode] = useState(searchParams.get('mode') === 'signup' ? 'signup' : 'login'); // 'login' | 'signup'
  const [loginField, setLoginField] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);

  const title = useMemo(() => (mode === 'login' ? 'Log in' : 'Create account'), [mode]);

  useEffect(() => {
    if (localStorage.getItem('token')) navigate(nextPath, { replace: true });
  }, [navigate, nextPath]);

  useEffect(() => {
    window.gtag?.("event", "page_view", {
      page_path: "/login",
      page_title: "Login",
    });
  }, []);

  const emailTouched = email.length > 0;
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit = isValidEmail && password && (mode === 'login' || name.trim());

  const loginTouched = loginField.length > 0;
  const isValidLogin = loginTouched ? (
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginField.trim()) || // email format
    /^[a-zA-Z0-9_-]{3,}$/.test(loginField.trim()) // username format (alphanumeric, dash, underscore, 3+ chars)
  ) : false;
  const canLoginSubmit = isValidLogin && password;

  async function onSubmit(e) {
    e.preventDefault();
    if (mode === 'login' && !canLoginSubmit) return;
    if (mode === 'signup' && !canSubmit) return;

    setBusy(true);
    try {
      if (mode === 'signup') {
        await signup(email.trim(), name.trim(), password);
        toast.success('Account created! Check your email to verify your account.');
        setMode('login');
        setPassword('');
        setName('');
        setEmail('');
        return;
      }

      setUnverifiedEmail(null);
      const preserveLocalWorkspace = isLocalMode();
      const data = await login(loginField.trim(), password);
      setStorageMode('sync');
      localStorage.setItem('token', data.token);
      localStorage.setItem('userId', data.userId);
      localStorage.setItem('anotequest_cache_owner', data.userId);
      localStorage.setItem('anotequest_premium', JSON.stringify(Boolean(data.isPremium)));

      // A login is always a pull from the account, never an upload from this
      // device.  In particular, a local-only workspace must not replace the
      // account's data merely because its owner signs in on this browser.
      // The local workspace remains in IndexedDB and can be reopened by
      // switching back to local-only mode.
      const resetKeys = [
        'userData',
        'userNotes',
        'anotequest_notes',
        'anotequest_folders',
        'anotequest_images',
        'anotequest_note_stickers',
        'anotequest_todos',
        'anotequest_sources',
        'anotequest_tables',
        'anotequest_drawings',
        'anotequest_trash',
        'anotequest_stats',
        'anotequest_stickers',
        'anotequest_username',
        'theme',
      ];
      for (const k of resetKeys) localStorage.removeItem(k);

      const hydratedNotes = notesFromServer(data.notes);
      localStorage.setItem('userNotes', JSON.stringify(hydratedNotes));
      localStorage.setItem('anotequest_notes', JSON.stringify(hydratedNotes));

      if (data.userData) {
        cacheUserDataToLocalStorage(data.userData);
      }

      if (preserveLocalWorkspace) {
        toast.success('Logged in. Your local-only workspace remains safely on this device.');
      }

      toast.success('Logged in');
      navigate(nextPath, { replace: true });
    } catch (err) {
      console.error('Login failed:', err);
      const msg = err?.message || 'Authentication failed';
      if (msg.includes('verify your email')) {
        setUnverifiedEmail(loginField.trim());
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-950 px-4 py-10 text-white">
      <img
        src="/landing_page_day.webp"
        alt="AnoteQuest canvas workspace"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-black/45" />
      <Card className="relative z-10 w-full max-w-md border-white/25 bg-white/15 p-6 text-white shadow-2xl backdrop-blur-xl">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-2 text-sm text-white/75">
          {mode === 'login' ? 'Welcome back.' : 'Save your notes across devices.'}
        </p>

        <form className="mt-6 space-y-4" onSubmit={onSubmit}>
          {mode === 'login' && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-white/90">Email or username</label>
              <Input
                value={loginField}
                onChange={(e) => setLoginField(e.target.value)}
                className={'border-white/25 bg-white/90 text-slate-950 placeholder:text-slate-500 ' + (loginTouched && !isValidLogin ? 'border-red-500 focus-visible:ring-red-500' : '')}
              />
              {loginTouched && !isValidLogin && (
                <p className="text-xs text-red-500">Please enter a valid email or username.</p>
              )}
            </div>
          )}

          {mode === 'signup' && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-white/90">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className={'border-white/25 bg-white/90 text-slate-950 placeholder:text-slate-500 ' + (emailTouched && !isValidEmail ? 'border-red-500 focus-visible:ring-red-500' : '')}
              />
              {emailTouched && !isValidEmail && (
                <p className="text-xs text-red-500">Please enter a valid email address.</p>
              )}
            </div>
          )}

          {mode === 'signup' && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-white/90">Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                placeholder="Your name"
                className="border-white/25 bg-white/90 text-slate-950 placeholder:text-slate-500"
              />
            </div>
          )}

          <div className="space-y-1">
            <label className="text-sm font-medium text-white/90">Password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              className="border-white/25 bg-white/90 text-slate-950"
            />
          </div>

          <Button className="w-full bg-white text-slate-950 hover:bg-white/90" type="submit" disabled={busy || (mode === 'login' ? !canLoginSubmit : !canSubmit)}>
            {busy ? 'Please wait…' : title}
          </Button>
        </form>

        <div className="mt-4 text-sm text-white/75">
          {mode === 'login' ? (
            <button className="underline" onClick={() => setMode('signup')} type="button">
              Need an account? Sign up
            </button>
          ) : (
            <button className="underline" onClick={() => setMode('login')} type="button">
              Already have an account? Log in
            </button>
          )}
        </div>

        <div className="mt-5 border-t border-white/20 pt-4 text-center text-sm text-white/75">
          <button
            className="underline underline-offset-4 hover:text-white"
            type="button"
            onClick={() => {
              const confirmed = window.confirm(
                'Continue locally? Your work will be stored only on this device and will not sync across devices. Local work above 20 MB cannot be automatically uploaded later unless you upgrade.'
              );
              if (!confirmed) return;
              setStorageMode('local');
              navigate('/app');
            }}
          >
            Or continue to the app locally
          </button>
          <p className="mt-2 text-xs text-white/60">Saved only on this device. No account or cross-device sync.</p>
        </div>

        {unverifiedEmail && (
          <div className="mt-4 rounded-md bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-200">
            <p className="mb-2">Didn't receive the verification email?</p>
            <button
              type="button"
              className="underline font-medium"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const res = await fetch(`${API_BASE}/api/auth/resend-verification`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: unverifiedEmail }),
                  });
                  const data = await res.json().catch(() => ({}));
                  toast.success(data?.message || 'Verification email sent.');
                  setUnverifiedEmail(null);
                } catch {
                  toast.error('Failed to resend. Please try again.');
                } finally {
                  setBusy(false);
                }
              }}
            >
              Resend verification email
            </button>
          </div>
        )}
      </Card>
    </main>
  );
}
